import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  AlertCircle,
  Bell,
  CheckCircle2,
  Clock3,
  RefreshCw,
  Send,
  Smartphone,
  Users,
} from "lucide-react";

import {
  fetchAdminNotificationEvents,
  fetchAdminNotificationOverview,
  fetchAdminNotificationTargets,
  sendAdminPush,
} from "../../lib/admin/adminNotifications";


const DAILY_MESSAGE =
  "Remember why you started: money, growth, freedom. Do today’s lesson.";


function formatDateTime(
  value
) {
  if (!value) {
    return "—";
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "—";
  }

  return date.toLocaleString(
    "en-ZA",
    {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }
  );
}


function MetricCard({
  icon: Icon,
  label,
  value,
  description,
}) {
  return (
    <div className="rounded-[24px] border border-[#EFEFEF] bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#FFF4EC] text-[#FF914D]">
          <Icon className="h-5 w-5" />
        </div>

        <p className="text-2xl font-black text-[#333333]">
          {value}
        </p>
      </div>

      <p className="mt-4 font-bold text-[#333333]">
        {label}
      </p>

      <p className="mt-1 text-sm text-[#757575]">
        {description}
      </p>
    </div>
  );
}


export default function AdminNotifications() {
  const [
    overview,
    setOverview,
  ] =
    useState({});

  const [
    targets,
    setTargets,
  ] =
    useState([]);

  const [
    events,
    setEvents,
  ] =
    useState([]);

  const [
    loading,
    setLoading,
  ] =
    useState(true);

  const [
    sending,
    setSending,
  ] =
    useState(false);

  const [
    error,
    setError,
  ] =
    useState("");

  const [
    notice,
    setNotice,
  ] =
    useState("");

  const [
    form,
    setForm,
  ] =
    useState({
      audience:
        "single_user",

      userId:
        "",

      title:
        "Gwin",

      message:
        "",

      url:
        "/dashboard",
    });


  const loadData =
    useCallback(
      async () => {
        try {
          setLoading(
            true
          );

          setError("");

          const [
            nextOverview,
            nextTargets,
            nextEvents,
          ] =
            await Promise.all([
              fetchAdminNotificationOverview(),
              fetchAdminNotificationTargets(),
              fetchAdminNotificationEvents(),
            ]);


          setOverview(
            nextOverview
          );

          setTargets(
            nextTargets
          );

          setEvents(
            nextEvents
          );
        } catch (
          loadError
        ) {
          console.error(
            "Admin notifications load failed:",
            loadError
          );

          setError(
            loadError
              ?.message ||
              "Notifications could not be loaded."
          );
        } finally {
          setLoading(
            false
          );
        }
      },
      []
    );


  useEffect(() => {
    loadData();
  }, [loadData]);


  const availableTargets =
    useMemo(
      () =>
        targets.filter(
          (target) =>
            target
              .notifications_enabled &&
            Number(
              target
                .active_device_count
            ) > 0
        ),
      [targets]
    );


  async function handleSend(
    event
  ) {
    event.preventDefault();

    setError("");
    setNotice("");


    if (
      !form.message
        .trim()
    ) {
      setError(
        "Enter a notification message."
      );

      return;
    }


    if (
      form.audience ===
        "single_user" &&
      !form.userId
    ) {
      setError(
        "Select a user."
      );

      return;
    }


    if (
      form.audience ===
      "all_enabled"
    ) {
      const confirmed =
        window.confirm(
          "Send this notification to every user who currently has notifications enabled?"
        );


      if (!confirmed) {
        return;
      }
    }


    try {
      setSending(
        true
      );


      const result =
        await sendAdminPush({
          audience:
            form.audience,

          userId:
            form.userId,

          title:
            form.title
              .trim() ||
            "Gwin",

          message:
            form.message
              .trim(),

          url:
            form.url
              .trim() ||
            "/dashboard",
        });


      setNotice(
        `Notification processed. ${Number(
          result.okCount || 0
        )} delivered, ${Number(
          result.failedCount || 0
        )} failed.`
      );


      setForm(
        (current) => ({
          ...current,
          message: "",
        })
      );


      await loadData();
    } catch (
      sendError
    ) {
      console.error(
        "Admin notification send failed:",
        sendError
      );

      setError(
        sendError?.message ||
          "The notification could not be sent."
      );
    } finally {
      setSending(
        false
      );
    }
  }


  return (
    <>
      <header className="mb-7">
        <p className="text-xs font-bold uppercase tracking-wider text-[#FF914D]">
          Admin · Notifications
        </p>

        <div className="mt-2 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-[34px] font-bold leading-tight tracking-[-0.03em] text-[#333333] md:text-[42px]">
              Notifications
            </h1>

            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-[#757575] md:text-base">
              Review notification
              availability and send
              approved Gwin push
              notifications.
            </p>
          </div>

          <button
            type="button"
            onClick={
              loadData
            }
            disabled={
              loading
            }
            className="inline-flex items-center gap-2 rounded-full border border-[#E5E5E5] bg-white px-4 py-2.5 text-sm font-bold text-[#333333]"
          >
            <RefreshCw
              className={[
                "h-4 w-4",
                loading
                  ? "animate-spin"
                  : "",
              ].join(" ")}
            />

            Refresh
          </button>
        </div>
      </header>


      {error && (
        <div className="mb-5 flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700">
          <AlertCircle className="h-5 w-5 shrink-0" />
          {error}
        </div>
      )}


      {notice && (
        <div className="mb-5 flex items-start gap-3 rounded-2xl border border-green-200 bg-green-50 p-4 text-sm font-semibold text-green-700">
          <CheckCircle2 className="h-5 w-5 shrink-0" />
          {notice}
        </div>
      )}


      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          icon={Bell}
          label="Enabled users"
          value={
            overview
              .notifications_enabled_count ||
            0
          }
          description="Users who have notifications switched on."
        />

        <MetricCard
          icon={
            CheckCircle2
          }
          label="Permission granted"
          value={
            overview
              .permission_granted_count ||
            0
          }
          description="Users with a granted notification permission state."
        />

        <MetricCard
          icon={Users}
          label="Subscribed users"
          value={
            overview
              .subscribed_user_count ||
            0
          }
          description="Users with at least one active push subscription."
        />

        <MetricCard
          icon={
            Smartphone
          }
          label="Active devices"
          value={
            overview
              .active_device_count ||
            0
          }
          description="Currently registered active push devices."
        />
      </section>


      <section className="mt-7 rounded-[24px] border border-[#EFEFEF] bg-white p-5 shadow-sm">
        <div className="flex items-start gap-4">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#FFF4EC] text-[#FF914D]">
            <Clock3 className="h-5 w-5" />
          </div>

          <div>
            <p className="font-black text-[#333333]">
              Automatic learner reminder
            </p>

            <p className="mt-1 text-sm text-[#757575]">
              Active · Daily at
              07:00 SAST
            </p>

            <div className="mt-4 rounded-2xl bg-[#FAFAFA] p-4 text-sm font-semibold text-[#333333]">
              {DAILY_MESSAGE}
            </div>
          </div>
        </div>
      </section>


      <section className="mt-7 rounded-[24px] border border-[#EFEFEF] bg-white p-5 shadow-sm">
        <h2 className="text-lg font-black text-[#333333]">
          Manual notification
        </h2>

        <p className="mt-1 text-sm text-[#757575]">
          Notifications are sent only
          to users who currently have
          notifications enabled and an
          active device subscription.
        </p>


        <form
          onSubmit={
            handleSend
          }
          className="mt-5 space-y-4"
        >
          <div>
            <label className="text-sm font-bold text-[#333333]">
              Audience
            </label>

            <select
              value={
                form.audience
              }
              onChange={(
                event
              ) =>
                setForm(
                  (current) => ({
                    ...current,

                    audience:
                      event
                        .target
                        .value,

                    userId:
                      "",
                  })
                )
              }
              className="mt-2 w-full rounded-2xl border border-[#E5E5E5] bg-white px-4 py-3 text-sm"
            >
              <option value="single_user">
                Single user
              </option>

              <option value="all_enabled">
                All enabled users
              </option>
            </select>
          </div>


          {form.audience ===
            "single_user" && (
            <div>
              <label className="text-sm font-bold text-[#333333]">
                User
              </label>

              <select
                value={
                  form.userId
                }
                onChange={(
                  event
                ) =>
                  setForm(
                    (current) => ({
                      ...current,

                      userId:
                        event
                          .target
                          .value,
                    })
                  )
                }
                className="mt-2 w-full rounded-2xl border border-[#E5E5E5] bg-white px-4 py-3 text-sm"
              >
                <option value="">
                  Select a user
                </option>

                {availableTargets.map(
                  (
                    target
                  ) => (
                    <option
                      key={
                        target.user_id
                      }
                      value={
                        target.user_id
                      }
                    >
                      {target.full_name}
                      {" · "}
                      {target.email ||
                        "No email"}
                      {" · "}
                      {target.active_device_count}
                      {" device"}
                      {Number(
                        target.active_device_count
                      ) === 1
                        ? ""
                        : "s"}
                    </option>
                  )
                )}
              </select>
            </div>
          )}


          <div>
            <label className="text-sm font-bold text-[#333333]">
              Title
            </label>

            <input
              value={
                form.title
              }
              onChange={(
                event
              ) =>
                setForm(
                  (current) => ({
                    ...current,

                    title:
                      event
                        .target
                        .value
                        .slice(
                          0,
                          100
                        ),
                  })
                )
              }
              className="mt-2 w-full rounded-2xl border border-[#E5E5E5] px-4 py-3 text-sm"
            />
          </div>


          <div>
            <label className="text-sm font-bold text-[#333333]">
              Message
            </label>

            <textarea
              value={
                form.message
              }
              onChange={(
                event
              ) =>
                setForm(
                  (current) => ({
                    ...current,

                    message:
                      event
                        .target
                        .value
                        .slice(
                          0,
                          500
                        ),
                  })
                )
              }
              rows={5}
              maxLength={500}
              className="mt-2 w-full rounded-2xl border border-[#E5E5E5] px-4 py-3 text-sm"
            />

            <p className="mt-1 text-right text-xs text-[#999999]">
              {form.message.length}/500
            </p>
          </div>


          <div>
            <label className="text-sm font-bold text-[#333333]">
              Destination
            </label>

            <input
              value={
                form.url
              }
              onChange={(
                event
              ) =>
                setForm(
                  (current) => ({
                    ...current,

                    url:
                      event
                        .target
                        .value
                        .slice(
                          0,
                          500
                        ),
                  })
                )
              }
              placeholder="/dashboard"
              className="mt-2 w-full rounded-2xl border border-[#E5E5E5] px-4 py-3 text-sm"
            />

            <p className="mt-1 text-xs text-[#757575]">
              Use a Gwin path such as
              /dashboard or /resources.
            </p>
          </div>


          <button
            type="submit"
            disabled={
              sending
            }
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FF914D] px-5 py-3 text-sm font-black text-white hover:bg-[#E9782F] disabled:opacity-50"
          >
            {sending ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}

            {sending
              ? "Sending…"
              : "Send notification"}
          </button>
        </form>
      </section>


      <section className="mt-7 overflow-hidden rounded-[24px] border border-[#EFEFEF] bg-white shadow-sm">
        <div className="border-b border-[#EFEFEF] p-5">
          <h2 className="font-black text-[#333333]">
            Recent manual sends
          </h2>
        </div>

        {events.length === 0 ? (
          <p className="p-6 text-sm text-[#757575]">
            No manual notification
            history yet.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-[1100px] w-full text-left text-sm">
              <thead className="bg-[#FFF4EC] text-xs uppercase text-[#757575]">
                <tr>
                  <th className="px-4 py-3">
                    When
                  </th>

                  <th className="px-4 py-3">
                    Audience
                  </th>

                  <th className="px-4 py-3">
                    Message
                  </th>

                  <th className="px-4 py-3">
                    Delivered
                  </th>

                  <th className="px-4 py-3">
                    Failed
                  </th>

                  <th className="px-4 py-3">
                    Result
                  </th>
                </tr>
              </thead>

              <tbody>
                {events.map(
                  (
                    event
                  ) => (
                    <tr
                      key={
                        event.event_id
                      }
                      className="border-t border-[#EFEFEF]"
                    >
                      <td className="px-4 py-4">
                        {formatDateTime(
                          event.created_at
                        )}
                      </td>

                      <td className="px-4 py-4 font-semibold">
                        {event.audience ===
                        "all_enabled"
                          ? "All enabled users"
                          : event.target_name ||
                            "Single user"}
                      </td>

                      <td className="max-w-[360px] px-4 py-4">
                        <p className="font-bold">
                          {event.title}
                        </p>

                        <p className="mt-1 text-xs text-[#757575]">
                          {event.message}
                        </p>
                      </td>

                      <td className="px-4 py-4 font-black text-green-700">
                        {event.success_count}
                      </td>

                      <td className="px-4 py-4 font-black text-red-600">
                        {event.failed_count}
                      </td>

                      <td className="px-4 py-4 font-bold">
                        {event.result_status}
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}